import React, { memo } from 'react';
import { Box, GlobalStyles, alpha, useTheme } from '@mui/material';
import { pdfFontFamily } from '../../utils/pdf';
import { cardSize } from './catalog';
import { CardTarget } from './render/primitives';
import IdCardFace, { type CardSide } from './render/IdCardFace';
import type { CardFaceProps } from './render/faces/shared';

const FONT_BASE = `${process.env.PUBLIC_URL || ''}/fonts/pdf`;

/** The card fonts for on-screen cards — the files the PDF embeds, served by
 *  the portal itself (never a CDN), so the preview sets type exactly as the
 *  print does. */
const cardFonts = (
  <GlobalStyles
    styles={{
      '@font-face': [
        ...([[400, 'Regular'], [500, 'Medium'], [600, 'SemiBold'], [700, 'Bold']] as const).map(([weight, file]) => ({
          fontFamily: pdfFontFamily.sans, fontWeight: weight, fontDisplay: 'swap', src: `url(${FONT_BASE}/Inter-${file}.ttf) format("truetype")`,
        })),
        ...([[400, 'Regular'], [500, 'Medium'], [700, 'Bold']] as const).map(([weight, file]) => ({
          fontFamily: pdfFontFamily.mono, fontWeight: weight, fontDisplay: 'swap', src: `url(${FONT_BASE}/JetBrainsMono-${file}.ttf) format("truetype")`,
        })),
      ] as any,
    }}
  />
);

/** A CR80 card's corner radius (3.18 mm) in points. */
const CORNER = 9;

interface Props extends CardFaceProps {
  side: CardSide;
  /** On-screen width in px; the card scales to it. */
  width: number;
  /** A lighter shadow, for cards in a list. */
  flat?: boolean;
}

/** A card on screen, drawn from the same faces as the printed PDF. */
const IdCardPreview: React.FC<Props> = ({ side, width, flat, ...face }) => {
  const theme = useTheme();
  const size = cardSize(face.design.layout);
  const scale = width / size.width;
  return (
    <Box
      sx={{
        width,
        height: size.height * scale,
        borderRadius: `${CORNER * scale}px`,
        overflow: 'hidden',
        flexShrink: 0,
        bgcolor: '#fff',
        boxShadow: flat
          ? `0 1px 2px ${alpha('#0f172a', 0.12)}, 0 0 0 1px ${alpha(theme.palette.text.primary, 0.06)}`
          : `0 18px 40px -12px ${alpha('#0f172a', 0.35)}, 0 4px 10px -2px ${alpha('#0f172a', 0.12)}, 0 0 0 1px ${alpha('#0f172a', 0.06)}`,
      }}
    >
      {cardFonts}
      <Box sx={{ width: size.width, height: size.height, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        <CardTarget target="html">
          <IdCardFace side={side} {...face} />
        </CardTarget>
      </Box>
    </Box>
  );
};

export default memo(IdCardPreview);
